import React from 'react';
import { Chip, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { ENV_LABEL, EnvLabel } from '../utils/envLabel';

interface EnvironmentBadgeProps {
  /** `expanded` sits beside the wordmark; `collapsed` stacks above the mini-drawer toggle. */
  variant?: 'expanded' | 'collapsed';
  /** Override the resolved label — for tests; production code uses the build-time value. */
  envLabel?: EnvLabel | null;
}

/**
 * Always-visible environment marker next to the sidebar wordmark (CAO-212).
 *
 * Renders nothing on an unlabelled (production) build, so production is pixel
 * identical to what it was before this component existed.
 */
const EnvironmentBadge: React.FC<EnvironmentBadgeProps> = ({
  variant = 'expanded',
  envLabel = ENV_LABEL,
}) => {
  const theme = useTheme();

  if (!envLabel) return null;

  const palette = theme.palette[envLabel.color];
  const collapsed = variant === 'collapsed';

  return (
    <Chip
      label={envLabel.label}
      size="small"
      aria-label={`${envLabel.label} environment`}
      sx={{
        height: collapsed ? 18 : 22,
        fontSize: collapsed ? '0.6rem' : '0.7rem',
        fontWeight: 600,
        letterSpacing: '0.05em',
        backgroundColor: palette.main,
        color: palette.contrastText ?? theme.palette.getContrastText(palette.main),
        // Faint halo so the marker still registers peripherally on a dark UI.
        boxShadow: `0 0 8px ${alpha(palette.main, 0.5)}`,
        '& .MuiChip-label': { px: collapsed ? 0.5 : 1 },
      }}
    />
  );
};

export default EnvironmentBadge;
